import { useEffect, useState } from "react";
import { AlertTriangle, ChevronLeft, ChevronRight, History, Search } from "lucide-react";
import {
  Badge, Button, Card, EmptyState, Modal, PageHeader,
  Select, SkeletonTableRows, Table, Td, Th, THead, Tr,
} from "../components/ui";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch } from "../lib/api";
import { dateTime } from "../lib/format";

interface AuditEntry {
  id: string;
  actorUserId: string | null;
  actorUsername: string | null;
  action: string;
  entity: string;
  entityId: string | null;
  before: unknown;
  after: unknown;
  metadata: unknown;
  ip: string | null;
  device: string | null;
  createdAt: string;
}

interface AuditPage {
  items: AuditEntry[];
  total: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 25;

function actionTone(action: string): "success" | "warning" | "danger" | "info" | "neutral" {
  if (action.endsWith(".deleted") || action.endsWith(".voided") || action.endsWith(".failed")) return "danger";
  if (action.endsWith(".created") || action.endsWith(".issued") || action.endsWith(".activated")) return "success";
  if (action.endsWith(".disabled") || action.endsWith(".regenerated") || action.endsWith(".amended")) return "warning";
  return "neutral";
}

/**
 * Read-only trail of state-changing actions — see routes/audit.ts. There is
 * intentionally no entry here for GET requests: only actions that mutated
 * something get audited, so this list is exactly the history of "what
 * changed", not a request log.
 */
export function AuditLogsPage() {
  const { language } = useBranding();
  const ar = language === "ar";

  const [data, setData] = useState<AuditPage | null>(null);
  const [actions, setActions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [selected, setSelected] = useState<AuditEntry | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    void apiFetch<{ items: string[] }>("/audit-logs/actions").then((r) => setActions(r.items)).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (actionFilter) params.set("action", actionFilter);
    if (entityFilter) params.set("entity", entityFilter);

    apiFetch<AuditPage>(`/audit-logs?${params.toString()}`)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((err) => { if (!cancelled) setError(err instanceof ApiError ? err.message : ar ? "تعذّر تحميل سجل التدقيق." : "Couldn't load the audit log."); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [page, actionFilter, entityFilter, ar, reloadTick]);

  const entities = Array.from(new Set(actions.map((a) => a.split(".")[0]))).sort();
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title={ar ? "سجل التدقيق" : "Audit log"} />
        <Card>
          <EmptyState icon={<AlertTriangle className="size-6" />} title={ar ? "تعذّر التحميل" : "Couldn't load"} description={error}
            action={<Button size="sm" onClick={() => setReloadTick((t) => t + 1)}>{ar ? "إعادة المحاولة" : "Retry"}</Button>} />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader title={ar ? "سجل التدقيق" : "Audit log"} subtitle={ar ? "كل إجراء غيّر بيانات — من نفّذه ومتى." : "Every action that changed data — who did it and when."} />

      <Card>
        <div className="grid gap-3 sm:grid-cols-2">
          <Select label={ar ? "الإجراء" : "Action"} value={actionFilter} onChange={(e) => { setPage(1); setActionFilter(e.target.value); }}>
            <option value="">{ar ? "كل الإجراءات" : "All actions"}</option>
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </Select>
          <Select label={ar ? "الكيان" : "Entity"} value={entityFilter} onChange={(e) => { setPage(1); setEntityFilter(e.target.value); }}>
            <option value="">{ar ? "كل الكيانات" : "All entities"}</option>
            {entities.map((e) => <option key={e} value={e}>{e}</option>)}
          </Select>
        </div>
      </Card>

      <Card padding="none">
        {!loading && data && data.items.length === 0 ? (
          <div className="p-5 sm:p-6">
            <EmptyState icon={<History className="size-6" />} title={ar ? "لا توجد سجلات" : "No entries"}
              description={ar ? "لم يُنفَّذ أي إجراء يطابق هذا التصفية بعد." : "No action matching this filter has happened yet."} />
          </div>
        ) : (
          <>
            <Table>
              <THead>
                <tr>
                  <Th>{ar ? "الوقت" : "Time"}</Th>
                  <Th>{ar ? "المستخدم" : "User"}</Th>
                  <Th>{ar ? "الإجراء" : "Action"}</Th>
                  <Th>{ar ? "الكيان" : "Entity"}</Th>
                  <Th />
                </tr>
              </THead>
              <tbody>
                {loading || !data ? (
                  <SkeletonTableRows rows={6} cols={5} />
                ) : (
                  data.items.map((entry) => (
                    <Tr key={entry.id}>
                      <Td className="tabular-nums text-muted-foreground">{dateTime(entry.createdAt, language)}</Td>
                      <Td>{entry.actorUsername ?? <span className="text-muted-subtle">{ar ? "النظام" : "System"}</span>}</Td>
                      <Td><Badge tone={actionTone(entry.action)}>{entry.action}</Badge></Td>
                      <Td className="text-muted-foreground">{entry.entity}{entry.entityId ? ` · ${entry.entityId.slice(0, 8)}` : ""}</Td>
                      <Td>
                        <Button size="sm" variant="ghost" icon={<Search className="size-3.5" />} onClick={() => setSelected(entry)}>
                          {ar ? "تفاصيل" : "Details"}
                        </Button>
                      </Td>
                    </Tr>
                  ))
                )}
              </tbody>
            </Table>

            {data && (
              <div className="flex items-center justify-between gap-3 border-t border-line/60 px-5 py-3 sm:px-6">
                <p className="text-xs text-muted-foreground">
                  {ar ? `الصفحة ${page} من ${totalPages} — ${data.total} سجل` : `Page ${page} of ${totalPages} — ${data.total} entries`}
                </p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={page <= 1}
                    icon={ar ? <ChevronRight className="size-4" /> : <ChevronLeft className="size-4" />}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    {ar ? "السابق" : "Previous"}
                  </Button>
                  <Button size="sm" variant="outline" disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                    {ar ? "التالي" : "Next"}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <Modal open={Boolean(selected)} onClose={() => setSelected(null)} title={ar ? "تفاصيل الحدث" : "Event details"} maxWidth="max-w-lg" closeLabel={ar ? "إغلاق" : "Close"}>
        {selected && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-muted-subtle">{ar ? "الوقت" : "Time"}</span><p className="tabular-nums text-ink-900">{dateTime(selected.createdAt, language)}</p></div>
              <div><span className="text-muted-subtle">{ar ? "المستخدم" : "User"}</span><p className="text-ink-900">{selected.actorUsername ?? "—"}</p></div>
              <div><span className="text-muted-subtle">{ar ? "عنوان IP" : "IP"}</span><p className="tabular-nums text-ink-900">{selected.ip ?? "—"}</p></div>
              <div><span className="text-muted-subtle">{ar ? "الجهاز" : "Device"}</span><p className="truncate text-ink-900">{selected.device ?? "—"}</p></div>
            </div>
            {selected.metadata != null && (
              <div>
                <p className="mb-1 text-xs font-medium text-ink-700">{ar ? "بيانات إضافية" : "Metadata"}</p>
                <pre className="max-h-32 overflow-auto rounded-lg bg-ink-50 p-2.5 text-xs text-ink-700 ltr:text-left rtl:text-right">{JSON.stringify(selected.metadata, null, 2)}</pre>
              </div>
            )}
            {selected.before != null && (
              <div>
                <p className="mb-1 text-xs font-medium text-ink-700">{ar ? "قبل" : "Before"}</p>
                <pre className="max-h-32 overflow-auto rounded-lg bg-ink-50 p-2.5 text-xs text-ink-700 ltr:text-left rtl:text-right">{JSON.stringify(selected.before, null, 2)}</pre>
              </div>
            )}
            {selected.after != null && (
              <div>
                <p className="mb-1 text-xs font-medium text-ink-700">{ar ? "بعد" : "After"}</p>
                <pre className="max-h-32 overflow-auto rounded-lg bg-ink-50 p-2.5 text-xs text-ink-700 ltr:text-left rtl:text-right">{JSON.stringify(selected.after, null, 2)}</pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
